import { useState, useEffect, useRef } from 'react';
import { auth } from '../firebase';

// WebLLM model list — needs WebGPU + enough VRAM
// 4GB VRAM: safe up to 3B  |  8GB VRAM: up to 7B  |  16GB+: any
export const WEBLLM_MODELS = [
  // ── Small (4GB VRAM) ──────────────────────────────────────────────────────
  { id: 'Llama-3.2-1B-Instruct-q4f16_1-MLC',  label: 'Llama 3.2 1B  — fast, low quality (~800MB)',   vram: '4GB' },
  { id: 'Llama-3.2-3B-Instruct-q4f16_1-MLC',  label: 'Llama 3.2 3B  — good balance (~2GB)',           vram: '4GB' },
  { id: 'gemma-2-2b-it-q4f16_1-MLC',           label: 'Gemma 2 2B   — good quality (~1.5GB)',          vram: '4GB' },
  { id: 'Phi-3.5-mini-instruct-q4f16_1-MLC',  label: 'Phi 3.5 Mini  — excellent for size (~2.4GB)',   vram: '4GB' },
  // ── Medium (8GB VRAM) ─────────────────────────────────────────────────────
  { id: 'Llama-3.1-8B-Instruct-q4f16_1-MLC',  label: 'Llama 3.1 8B  — high quality (~5GB)',           vram: '8GB' },
  { id: 'Mistral-7B-Instruct-v0.3-q4f16_1-MLC', label: 'Mistral 7B  — high quality (~4.5GB)',         vram: '8GB' },
  { id: 'Qwen2.5-7B-Instruct-q4f16_1-MLC',    label: 'Qwen 2.5 7B   — high quality (~4.5GB)',         vram: '8GB' },
  // ── Large (16GB VRAM) ─────────────────────────────────────────────────────
  { id: 'Llama-3.3-70B-Instruct-q3f16_1-MLC', label: 'Llama 3.3 70B — best quality (~35GB)',          vram: '16GB' },
];

export const SYSTEM_PROMPT =
  'You are a Bible study assistant. ' +
  'Answer questions using ONLY the Scripture passages provided in the user message. ' +
  'Never draw on knowledge from outside those passages.';

/**
 * useLLM — users can explicitly choose between Ollama and WebLLM.
 *
 * Returns:
 *   activeProvider     : 'ollama' | 'webllm'          — currently selected by user
 *   setActiveProvider  : fn                            — lets user switch
 *   ollamaAvailable    : bool                         — whether Ollama was detected
 *   webgpuAvailable    : bool                         — whether WebGPU is supported
 *   detecting          : bool                         — still running initial checks
 *   ollamaModels       : string[]
 *   ollamaModel        : string                       — selected Ollama model
 *   setOllamaModel     : fn
 *   webllmStatus       : 'idle'|'loading'|'ready'|'error'
 *   webllmModel        : string                       — loaded WebLLM model id
 *   loadProgress       : 0–100
 *   loadText           : string
 *   loadWebLLMModel    : (modelId) => void
 *   generate           : ({ messages, onToken, onDone, onError }) => Promise<void>
 *   isReady            : bool
 *   cancel             : () => void
 */
const PROVIDER_KEY = 'plutobible-ai-provider';

export function useLLM() {
  const [detecting, setDetecting]           = useState(true);
  const [ollamaAvailable, setOllamaAvailable] = useState(false);
  const [webgpuAvailable, setWebgpuAvailable] = useState(false);
  const [activeProvider, setActiveProvider] = useState(
    () => localStorage.getItem(PROVIDER_KEY) || 'ollama'
  );

  const [ollamaModels, setOllamaModels]     = useState([]);
  const [ollamaModel, setOllamaModel]       = useState('');

  const [webllmStatus, setWebllmStatus]     = useState('idle');
  const [webllmModel, setWebllmModel]       = useState('');
  const [loadProgress, setLoadProgress]     = useState(0);
  const [loadText, setLoadText]             = useState('');

  const engineRef = useRef(null);
  const cancelRef = useRef(false);

  const LAST_MODEL_KEY = 'plutobible-webllm-last-model';

  // ── Availability detection (runs once on mount) ──────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Check Ollama
      try {
        const res = await fetch('http://localhost:11434/api/tags', {
          signal: AbortSignal.timeout(2000),
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          const models = (data.models || []).map(m => m.name);
          setOllamaModels(models);
          setOllamaModel(models[0] || '');
          setOllamaAvailable(true);
        }
      } catch {
        // Ollama not running
      }

      // Check WebGPU
      if (!cancelled && navigator.gpu) {
        setWebgpuAvailable(true);
        // Auto-reload previously used model from browser cache
        const lastModel = localStorage.getItem(LAST_MODEL_KEY);
        if (!cancelled && lastModel) {
          _loadModel(lastModel);
        }
      }

      if (!cancelled) setDetecting(false);
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── WebLLM model loader ──────────────────────────────────────────────────────
  async function _loadModel(modelId) {
    setWebllmStatus('loading');
    setLoadProgress(0);
    setLoadText('Initializing…');
    try {
      const { CreateMLCEngine } = await import('@mlc-ai/web-llm');
      const engine = await CreateMLCEngine(modelId, {
        initProgressCallback: (report) => {
          setLoadProgress(Math.round((report.progress || 0) * 100));
          setLoadText(report.text || '');
        },
      });
      engineRef.current = engine;
      setWebllmModel(modelId);
      setWebllmStatus('ready');
    } catch (e) {
      console.error('WebLLM load error:', e);
      setWebllmStatus('error');
    }
  }

  function loadWebLLMModel(modelId) {
    localStorage.setItem(LAST_MODEL_KEY, modelId);
    return _loadModel(modelId);
  }

  // ── Provider persistence ──────────────────────────────────────────────────────
  function switchProvider(provider) {
    localStorage.setItem(PROVIDER_KEY, provider);
    setActiveProvider(provider);
  }

  // ── Generation ───────────────────────────────────────────────────────────────
  async function generate({ messages, onToken, onDone, onError, onUsage }) {
    cancelRef.current = false;
    if (activeProvider === 'deepseek') {
      await _generateDeepSeek(messages, onToken, onDone, onError, onUsage);
    } else if (activeProvider === 'ollama') {
      await _generateOllama(messages, onToken, onDone, onError);
    } else if (activeProvider === 'webllm' && webllmStatus === 'ready') {
      await _generateWebLLM(messages, onToken, onDone, onError);
    } else {
      onError?.(new Error('Selected provider is not ready.'));
    }
  }

  async function _generateDeepSeek(messages, onToken, onDone, onError, onUsage) {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) throw new Error('Sign in to use PlutoBible AI.');

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8002';
      const res = await fetch(`${apiUrl}/bible/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ messages }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Server error ${res.status}`);
      }

      // Read usage from response headers (exposed via Access-Control-Expose-Headers on server)
      const usedHeader  = res.headers.get('X-AI-Messages-Used');
      const limitHeader = res.headers.get('X-AI-Messages-Limit');
      if (usedHeader !== null) {
        onUsage?.({ used: parseInt(usedHeader, 10), limit: parseInt(limitHeader || '300', 10) });
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        if (cancelRef.current) { reader.cancel(); break; }
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop(); // keep incomplete last line for next iteration

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') { onDone?.(); return; }
          try {
            const parsed = JSON.parse(data);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.token) onToken(parsed.token);
          } catch (parseErr) {
            if (parseErr.message !== 'Unexpected token') throw parseErr;
          }
        }
      }
      onDone?.();
    } catch (e) {
      onError?.(e);
    }
  }

  async function _generateOllama(messages, onToken, onDone, onError) {
    try {
      const res = await fetch('http://localhost:11434/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: ollamaModel, messages, stream: true }),
      });
      if (!res.ok) throw new Error(`Ollama error: ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        if (cancelRef.current) { reader.cancel(); break; }
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        for (const line of chunk.split('\n')) {
          if (!line.trim()) continue;
          try {
            const data = JSON.parse(line);
            if (data.message?.content) onToken(data.message.content);
            if (data.done) onDone?.();
          } catch { /* partial line */ }
        }
      }
    } catch (e) {
      onError?.(e);
    }
  }

  async function _generateWebLLM(messages, onToken, onDone, onError) {
    try {
      const stream = await engineRef.current.chat.completions.create({
        messages,
        stream: true,
        max_tokens: 512,
        temperature: 0.7,
      });
      let tokenCount = 0;
      for await (const chunk of stream) {
        if (cancelRef.current) break;
        const delta = chunk.choices[0]?.delta?.content;
        if (delta) { onToken(delta); tokenCount++; }
      }
      console.log(`[WebLLM] generation done — ${tokenCount} tokens`);
      onDone?.();
    } catch (e) {
      console.error('[WebLLM] generation error:', e);
      onError?.(e);
    }
  }

  function cancel() {
    cancelRef.current = true;
  }

  const isReady =
    activeProvider === 'deepseek' ||
    (activeProvider === 'ollama' && ollamaAvailable && ollamaModel) ||
    (activeProvider === 'webllm' && webllmStatus === 'ready');

  return {
    detecting,
    activeProvider,
    setActiveProvider: switchProvider,
    ollamaAvailable,
    webgpuAvailable,
    ollamaModels,
    ollamaModel,
    setOllamaModel,
    webllmStatus,
    webllmModel,
    loadProgress,
    loadText,
    loadWebLLMModel,
    generate,
    isReady,
    cancel,
  };
}
